import { dataTestId } from 'modules/experiments/constants';
import { useTakeoverExperimentContext, useVariantCardContext } from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { useRef, useState } from 'react';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Edit16 } from 'ui/icons';
import { Skeleton } from 'ui/skeleton';
import { colors } from 'ui/theme';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import {
  EllipsisText,
  STOIcon,
  StyledVariantNameInput,
  StyledVariantNameWrapper,
  VariantEditbutton,
} from './Styled';

type VariantCardBadgeProps = {
  isControl: boolean;
  isSTOExperiment: boolean;
};

const VariantCardBadge = ({ isControl, isSTOExperiment }: VariantCardBadgeProps) => {
  const { t } = useExperimentsTranslations();

  if (isControl) return <span>&nbsp;({t('badge.control')})</span>;
  if (isSTOExperiment && !isControl) return <STOIcon />;
  return <></>;
};

export const VariantName = () => {
  const { t } = useExperimentsTranslations();
  const { variant, isCardInEditMode, renamingCard, renameCard } = useVariantCardContext();
  const { isSTOExperiment } = useTakeoverExperimentContext();
  const { name: variantName, control: isControl } = variant;
  const [isVariantNameInEditMode, setIsVariantNameInEditMode] = useState(false);
  const [editableName, setEditableName] = useState(variantName);
  const unsavedChanges = variantName !== editableName;
  const inputRef = useRef<any>(null);

  const handleExitInput = () => {
    if (!editableName.trim()) {
      return;
    }
    if (unsavedChanges && isVariantNameInEditMode) {
      setIsVariantNameInEditMode(false);
      renameCard(editableName);
    }
  };

  useUnsavedChangesPrompt(unsavedChanges);
  useClickOutside(inputRef, handleExitInput);

  if (isControl || isSTOExperiment || !isCardInEditMode)
    return (
      <>
        <EllipsisText>{variantName}</EllipsisText>
        <VariantCardBadge isControl={isControl} isSTOExperiment={isSTOExperiment} />
      </>
    );

  if (renamingCard) {
    return <Skeleton width={350} height={30} />;
  }

  return isVariantNameInEditMode ? (
    <StyledVariantNameWrapper onClick={(e) => e.stopPropagation()}>
      <StyledVariantNameInput
        autoFocus
        data-test={dataTestId.variantNameEditInput}
        maxLength={100}
        placeholder={t('placeholder.variantName')}
        value={editableName}
        onChange={setEditableName}
        isRequired
      />
    </StyledVariantNameWrapper>
  ) : (
    <>
      <EllipsisText>{variantName}</EllipsisText>
      <VariantEditbutton
        data-test={dataTestId.variantNameEditButton}
        color={colors.textDisabled}
        onClick={(e) => {
          e.stopPropagation();
          setIsVariantNameInEditMode(true);
        }}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <Edit16 color={colors.grey50} />
      </VariantEditbutton>
    </>
  );
};
